import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useLanguage } from '../contexts/LanguageContext'
import { useExtensionStatus } from '../hooks/useAppNavigation'
import {
  Link as LinkIcon, Folder, Inventory, PlayCircle, MenuBook,
  ArrowRight, AutoAwesome, Publish, Extension
} from '@mui/icons-material'
import { Header } from '../components/Header'
import { buildAuthModalPath, resolveApiBaseUrl, resolveAuthToken } from '../lib/utils'
import { formatTaskDate } from '../lib/taskCenter'

type WorkbenchItem = {
  id: string
  product_id: string
  platform: string
  title: string
  thumbnail?: string | null
  status_category: 'processing' | 'ready' | 'uploaded' | 'failed'
  created_at?: string | null
  updated_at?: string | null
}

export const WorkspaceScreen = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useLanguage()
  const { status: extensionStatus } = useExtensionStatus()
  const [recentImports, setRecentImports] = useState<WorkbenchItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [requiresAuth, setRequiresAuth] = useState(false)
  const hasRecentImports = recentImports.length > 0

  const statusMeta = useMemo(
    () => ({
      processing: { label: t('workspace.status.processing'), className: 'bg-primary' },
      ready: { label: t('workspace.status.ready'), className: 'bg-emerald-500' },
      uploaded: { label: t('workspace.status.uploaded'), className: 'bg-slate-800' },
      failed: { label: t('workspace.status.failed'), className: 'bg-rose-500' }
    }),
    [t]
  )

  const openAuthModal = (mode: 'login' | 'signup') => {
    navigate(buildAuthModalPath(location.pathname, location.search, mode))
  }

  const loadRecentImports = useCallback(async () => {
    if (typeof window === 'undefined') {
      return
    }
    setIsLoading(true)
    setLoadError('')
    const token = resolveAuthToken()
    if (!token) {
      setRecentImports([])
      setRequiresAuth(true)
      setIsLoading(false)
      return
    }

    setRequiresAuth(false)
    try {
      const apiBaseUrl = resolveApiBaseUrl()
      const response = await fetch(`${apiBaseUrl}/api/v1/collections/workbench?limit=6`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
      if (!response.ok) {
        setRecentImports([])
        if (response.status === 401) {
          setRequiresAuth(true)
        } else {
          setLoadError(t('workspace.loadFailed'))
        }
        return
      }
      const payload = await response.json().catch(() => null)
      const items = Array.isArray(payload?.data) ? payload.data : []
      setRecentImports(items.slice(0, 6))
    } catch (error) {
      setRecentImports([])
      setLoadError(t('workspace.loadFailed'))
    } finally {
      setIsLoading(false)
    }
  }, [t])

  useEffect(() => {
    loadRecentImports()
  }, [loadRecentImports])

  useEffect(() => {
    const handleAuthUpdated = () => {
      loadRecentImports()
    }
    const handleFocus = () => {
      loadRecentImports()
    }
    window.addEventListener('auth:updated', handleAuthUpdated)
    window.addEventListener('focus', handleFocus)
    return () => {
      window.removeEventListener('auth:updated', handleAuthUpdated)
      window.removeEventListener('focus', handleFocus)
    }
  }, [loadRecentImports])

  const emptyTitle = requiresAuth ? t('workspace.loginTitle') : t('workspace.emptyTitle')
  const emptyDesc = requiresAuth ? t('workspace.loginDesc') : t('workspace.emptyDesc')
  const emptyActionLabel = requiresAuth ? t('workspace.loginAction') : t('workspace.startBtn')
  const handleEmptyAction = () => {
    if (requiresAuth) {
      openAuthModal('login')
    } else {
      navigate('/import')
    }
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header activeNav="workspace" />

      <main className="max-w-6xl mx-auto px-6 py-12">
        {extensionStatus !== 'CONNECTED' && (
          <div className="mb-8 bg-white border border-slate-200 rounded-2xl p-4 flex flex-col md:flex-row items-center justify-between gap-4 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center text-primary">
                <Extension />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-slate-900">{t('workspace.extRequired')}</span>
                  <span className="bg-amber-100 text-amber-600 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase">{t('workspace.extNotFound')}</span>
                </div>
                <p className="text-xs text-slate-500">{t('workspace.extDesc')}</p>
              </div>
            </div>
            <button
              onClick={() => alert('Downloading extension...')}
              className="px-4 py-2 bg-slate-900 text-white rounded-lg font-semibold text-xs transition-all hover:bg-slate-800 active:scale-95 shadow-sm"
            >
              {t('workspace.extDownload')}
            </button>
          </div>
        )}

        {hasRecentImports ? (
          <div className="mb-12 bg-white border border-slate-200 rounded-[2.5rem] p-8 md:p-10 shadow-sm flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-primary/60 mb-3">{t('workspace.returningLabel')}</p>
              <h1 className="text-3xl md:text-4xl font-black tracking-tight text-slate-900 mb-3">{t('workspace.returningTitle')}</h1>
              <p className="text-sm md:text-base text-slate-500 max-w-xl">{t('workspace.returningSub')}</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => navigate('/import')}
                className="px-8 py-4 bg-primary text-white rounded-2xl font-bold text-base shadow-lg shadow-blue-500/25 hover:bg-blue-700 transition-all active:scale-[0.98]"
              >
                {t('workspace.startBtn')}
              </button>
              <button
                onClick={() => navigate('/task-center')}
                className="px-8 py-4 bg-white border border-slate-200 text-slate-700 rounded-2xl font-bold text-base shadow-sm hover:bg-slate-50 transition-all active:scale-[0.98]"
              >
                {t('workspace.taskCenterEntry')}
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center text-center py-20 bg-[radial-gradient(circle_at_center,rgba(37,99,235,0.08)_0%,transparent_70%)] rounded-[3rem] mb-20">
            <div className="relative w-64 h-64 mb-10 flex items-center justify-center">
              <div className="absolute inset-0 bg-primary/5 rounded-full animate-pulse"></div>
              <div className="relative z-10 w-48 h-48 bg-white rounded-3xl shadow-2xl border border-slate-100 flex items-center justify-center overflow-hidden">
                <div className="grid grid-cols-3 gap-2 p-6 w-full">
                  <div className="h-12 bg-blue-50 rounded-lg"></div>
                  <div className="h-12 bg-blue-100 rounded-lg col-span-2"></div>
                  <div className="h-12 bg-blue-500 rounded-lg col-span-2 flex items-center justify-center text-white"><AutoAwesome fontSize="small" /></div>
                  <div className="h-12 bg-blue-50 rounded-lg"></div>
                  <div className="h-12 bg-blue-50 rounded-lg"></div>
                  <div className="h-12 bg-blue-50 rounded-lg col-span-2"></div>
                </div>
              </div>
              <div className="absolute top-0 right-0 w-12 h-12 bg-white rounded-xl shadow-lg border border-slate-100 flex items-center justify-center rotate-12 text-primary">
                <LinkIcon />
              </div>
              <div className="absolute bottom-4 left-0 w-12 h-12 bg-white rounded-xl shadow-lg border border-slate-100 flex items-center justify-center -rotate-12 text-indigo-500">
                <Publish />
              </div>
            </div>
            <h1 className="text-5xl font-black tracking-tight text-slate-900 mb-6">{t('workspace.heroTitle')}</h1>
            <p className="text-xl text-slate-500 max-w-2xl mb-10 leading-relaxed">{t('workspace.heroSub')}</p>
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <button onClick={() => navigate('/import')} className="px-10 py-5 bg-primary text-white rounded-2xl font-bold text-lg shadow-2xl shadow-blue-500/40 hover:bg-blue-700 hover:-translate-y-1 transition-all flex items-center gap-3 active:scale-95">
                {t('workspace.startBtn')} <ArrowRight />
              </button>
              <button onClick={() => navigate('/task-center')} className="px-10 py-5 bg-white border border-slate-200 text-slate-700 rounded-2xl font-bold text-lg shadow-sm hover:bg-slate-50 hover:-translate-y-0.5 transition-all active:scale-95">
                {t('workspace.taskCenterEntry')}
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-20 w-full max-w-4xl">
              {[
                { icon: <LinkIcon />, title: t('workspace.pasteLink'), desc: t('workspace.pasteDesc'), color: 'text-primary', bg: 'bg-blue-50' },
                { icon: <Folder />, title: t('workspace.uploadMedia'), desc: t('workspace.uploadDesc'), color: 'text-indigo-600', bg: 'bg-indigo-50' },
                { icon: <Inventory />, title: t('workspace.batchImport'), desc: t('workspace.batchDesc'), color: 'text-emerald-600', bg: 'bg-emerald-50' }
              ].map((item, i) => (
                <div key={i} onClick={() => navigate('/import')} className="p-6 bg-white border border-slate-200 rounded-2xl text-left hover:border-primary/50 transition-colors cursor-pointer group active:scale-[0.98]">
                  <div className={`w-12 h-12 rounded-xl ${item.bg} ${item.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>{item.icon}</div>
                  <h3 className="font-bold text-slate-900 mb-1">{item.title}</h3>
                  <p className="text-sm text-slate-500">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <section>
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl font-black text-slate-900 tracking-tight">{t('workspace.recentImports')}</h2>
            <button
              onClick={() => (requiresAuth ? openAuthModal('login') : navigate('/task-center'))}
              className="text-primary text-sm font-bold hover:underline hover:text-blue-700 transition-colors"
            >
              {t('workspace.viewHistory')}
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {isLoading ? (
              Array.from({ length: 3 }).map((_, index) => (
                <div key={`skeleton-${index}`} className="bg-white border border-slate-200 rounded-[2rem] p-4 animate-pulse">
                  <div className="aspect-square rounded-2xl bg-slate-100 mb-5" />
                  <div className="h-4 bg-slate-100 rounded w-3/4 mb-3" />
                  <div className="h-3 bg-slate-100 rounded w-1/2" />
                </div>
              ))
            ) : loadError ? (
              <div className="col-span-full bg-white border border-slate-200 rounded-2xl p-8 text-center">
                <div className="text-slate-900 font-semibold mb-2">{loadError}</div>
                <p className="text-sm text-slate-500 mb-4">{t('workspace.loadHint')}</p>
                <button
                  onClick={loadRecentImports}
                  className="px-4 py-2 bg-slate-900 text-white rounded-lg text-xs font-semibold hover:bg-slate-800 transition-colors"
                >
                  {t('workspace.retry')}
                </button>
              </div>
            ) : recentImports.length === 0 ? (
              <div className="col-span-full bg-white border border-slate-200 rounded-2xl p-8 text-center">
                <div className="text-slate-900 font-semibold mb-2">{emptyTitle}</div>
                <p className="text-sm text-slate-500 mb-4">{emptyDesc}</p>
                <button
                  onClick={handleEmptyAction}
                  className="px-4 py-2 bg-primary text-white rounded-lg text-xs font-semibold hover:bg-blue-700 transition-colors"
                >
                  {emptyActionLabel}
                </button>
              </div>
            ) : (
              recentImports.map((item) => {
                const status = item.status_category ?? 'processing'
                const meta = statusMeta[status] ?? { label: status, className: 'bg-slate-400' }
                const timeLabel = formatTaskDate(item.updated_at ?? item.created_at)
                const sourceLabel = item.platform || t('workspace.unknownSource')
                return (
                  <div
                    key={item.id}
                    onClick={() => navigate('/studio', { state: { collectionId: item.id } })}
                    className="group bg-white border border-slate-200 rounded-[2rem] p-4 hover:shadow-xl transition-all cursor-pointer active:scale-[0.98]"
                  >
                    <div className="aspect-square rounded-2xl overflow-hidden mb-5 bg-slate-50 relative">
                      {item.thumbnail ? (
                        <img className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" src={item.thumbnail} alt={item.title} />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-300">
                          <Inventory fontSize="large" />
                        </div>
                      )}
                      <div className={`absolute top-4 right-4 text-[10px] font-black uppercase px-3 py-1 rounded-full text-white shadow-lg ${meta.className}`}>
                        {meta.label}
                      </div>
                    </div>
                    <h4 className="font-bold text-slate-900 mb-1 truncate px-2">{item.title}</h4>
                    <p className="text-xs text-slate-400 font-bold uppercase tracking-widest px-2">{sourceLabel} - {timeLabel}</p>
                  </div>
                )
              })
            )}
          </div>
        </section>
      </main>

      <footer className="mt-20 flex items-center justify-center gap-8 py-10 border-t border-slate-100">
        <button
          onClick={() => alert('Opening video tutorial...')}
          className="flex items-center gap-2 text-sm font-bold text-slate-400 hover:text-primary transition-colors active:scale-95"
        >
          <PlayCircle fontSize="small" /> {t('workspace.watchTutorial')}
        </button>
        <div className="w-1.5 h-1.5 rounded-full bg-slate-200"></div>
        <button
          onClick={() => alert('Opening user guide...')}
          className="flex items-center gap-2 text-sm font-bold text-slate-400 hover:text-primary transition-colors active:scale-95"
        >
          <MenuBook fontSize="small" /> {t('workspace.readGuide')}
        </button>
      </footer>
    </div>
  )
}
